/** @fileoverview Energy Sources settings tab: map ENERGY KPIs to an energy source. */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { usePermission } from "../../../hooks/usePermission";
import { getLatestFY } from "../../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import { questionMatchesSteps, getNextEligibleFilters } from "../Components/TagFilterUtils";
import { PrimaryButton } from "../../../globalComponents/ui/Button";
import Spinner from "../../../globalComponents/ui/Spinner";
import SimpleDropdown from "../../../globalComponents/form/dropdowns/SimpleDropdown";
import ViewOnlyBanner from "../Components/ViewOnlyBanner";
import { useEnergySourcesData } from "./useEnergySourcesData";
import { saveKpiSourceMappings } from "./energySourcesApi";
import { safeParseJSON, buildChangedMappings } from "./helpers";
import SourceLegend from "./SourceLegend";
import KpiFilterBar from "./KpiFilterBar";
import BulkSourceAssignBar from "./BulkSourceAssignBar";
import KpiListRow from "./KpiListRow";
import SelectedKpisAccordion from "./SelectedKpisAccordion";
import "./EnergySources.css";

/**
 * Energy Sources settings page: pick a per-KPI energy source per financial year.
 * @returns {JSX.Element} The page.
 */
export default function EnergySources() {
  const [canUpdate] = usePermission("energy-sources.update", "ENERGY_SOURCES");

  const fyData = useMemo(() => safeParseJSON(localStorage.getItem("financialYears"), []), []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);

  const {
    allQuestions, sources, serverKpiSourceMap, selectableIds,
    loading, error, loadToken, refresh, reload,
  } = useEnergySourcesData(selectedFY?.id);

  const [savedIds, setSavedIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [kpiSourceMap, setKpiSourceMap] = useState({});
  const [savedKpiSourceMap, setSavedKpiSourceMap] = useState({});
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [kpiView, setKpiView] = useState("list");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState(null);

  const prevLeafKey = useRef(null);

  const isViewOnly = selectedFY?.id !== latestFY?.id;
  const isDisableEdit = isViewOnly || !canUpdate;

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    setSavedIds(new Set(selectableIds));
    setPendingIds(new Set(selectableIds));
    setKpiSourceMap(serverKpiSourceMap);
    setSavedKpiSourceMap(serverKpiSourceMap);
    setFilterSteps([]);
    setSearch("");
    setKpiView("list");
    prevLeafKey.current = null;
  }, [loadToken]);

  const activeSteps = filterSteps.filter(s => s.values?.size);

  const sourceAssignedCount = useMemo(() =>
    allQuestions.filter(q => pendingIds.has(q.questionId) && !!kpiSourceMap[q.questionId]).length,
    [allQuestions, pendingIds, kpiSourceMap]
  );

  const nextEligible = useMemo(() => getNextEligibleFilters(allQuestions, filterSteps), [allQuestions, filterSteps]);
  const isLeaf = activeSteps.length > 0 && nextEligible.length === 0;

  const leafQuestions = useMemo(() => {
    if (!isLeaf) return [];
    return allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
  }, [isLeaf, allQuestions, filterSteps]);

  const handleSelectFiltered = useCallback((steps) => {
    if (isDisableEdit) return;
    const active = steps.filter(s => s.values?.size);
    if (!active.length) return;
    const matching = allQuestions.filter(q => questionMatchesSteps(q, steps));
    if (!matching.length) return;
    setPendingIds(prev => {
      const next = new Set(prev);
      matching.forEach(q => next.add(q.questionId));
      return next;
    });
  }, [allQuestions, isDisableEdit]);

  const filteredQuestions = useMemo(() => {
    let qs = allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
    if (search.trim()) {
      const s = search.toLowerCase();
      qs = qs.filter(q => (q.title || "").toLowerCase().includes(s) || (q.moduleName || "").toLowerCase().includes(s));
    }
    return qs;
  }, [allQuestions, filterSteps, search]);

  const showKpiList = isLeaf || search.trim().length > 0;
  const visibleKpis = showKpiList ? filteredQuestions : [];

  useEffect(() => {
    if (isDisableEdit || !isLeaf || leafQuestions.length === 0) return;
    const leafKey = filterSteps.map(s => `${s.key}:${[...s.values].join(",")}`).join("|");
    if (prevLeafKey.current === leafKey) return;
    prevLeafKey.current = leafKey;
    setPendingIds(prev => { const next = new Set(prev); leafQuestions.forEach(q => next.add(q.questionId)); return next; });
  }, [isLeaf, leafQuestions, filterSteps, isDisableEdit]);

  const toggleKpi = useCallback((id, checked) => {
    if (isDisableEdit) return;
    setPendingIds(p => { const n = new Set(p); checked ? n.add(id) : n.delete(id); return n; });
    if (!checked) {
      setKpiSourceMap(prev => { const next = { ...prev }; delete next[id]; return next; });
    }
  }, [isDisableEdit]);

  const handleKpiSourceChange = useCallback((kpiId, sourceId) => {
    if (isDisableEdit) return;
    setKpiSourceMap(prev => {
      const next = { ...prev };
      if (!sourceId) delete next[kpiId];
      else next[kpiId] = sourceId;
      return next;
    });
  }, [isDisableEdit]);

  const hasPendingChanges = useMemo(() => {
    if (isDisableEdit) return false;
    if (pendingIds.size !== savedIds.size) return true;
    for (const id of pendingIds) if (!savedIds.has(id)) return true;
    const allKeys = new Set([...Object.keys(kpiSourceMap).map(Number), ...Object.keys(savedKpiSourceMap).map(Number)]);
    for (const k of allKeys) if (kpiSourceMap[k] !== savedKpiSourceMap[k]) return true;
    return false;
  }, [pendingIds, savedIds, kpiSourceMap, savedKpiSourceMap, isDisableEdit]);

  const handleSave = async () => {
    if (isDisableEdit) return;
    setSaving(true);
    try {
      const changed = buildChangedMappings(pendingIds, kpiSourceMap, savedKpiSourceMap);
      const res = changed.length === 0
        ? { data: { success: true } }
        : await saveKpiSourceMappings(selectedFY?.id, changed);

      if (res?.data?.success) {
        setSavedIds(new Set(pendingIds));
        setSavedKpiSourceMap({ ...kpiSourceMap });
        setSaved(true);
        setFilterSteps([]);
        setSearch("");
        prevLeafKey.current = null;
        showToast("Preferences saved!");
        setTimeout(() => setSaved(false), 2000);
        refresh();
      } else {
        showToast("Save failed — please try again", "error");
      }
    } catch (e) {
      console.error(e);
      showToast("Error saving", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size={32} color="var(--es-accent)" />
        <span style={{ fontSize: 13, color: "var(--es-slate-400)" }}>Loading energy source preferences…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 80, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <span style={{ fontSize: 13, color: "var(--es-red-600)", fontWeight: 600 }}>{error}</span>
        <PrimaryButton onClick={reload}>Retry</PrimaryButton>
      </div>
    );
  }

  return (
    <div className="es-root">
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "error" ? "var(--es-red-600)" : "var(--es-green-600)",
          color: "var(--es-white)", padding: "11px 18px", borderRadius: 10, zIndex: 1000,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px var(--es-shadow-lg)",
          animation: "es-toast-in 0.25s ease", display: "flex", alignItems: "center", gap: 8
        }}>
          <span>{toast.type === "error" ? "✕" : "✓"}</span>{toast.msg}
        </div>
      )}

      <div style={{
        background: "var(--es-white)", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid var(--es-slate-200)", boxShadow: "0 1px 3px var(--es-shadow-sm)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, var(--es-accent), var(--es-blue-600))" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--es-slate-900)" }}>Energy Source Selection</div>
            <div style={{ fontSize: 11, color: "var(--es-slate-400)" }}>
              {pendingIds.size} KPI{pendingIds.size !== 1 ? "s" : ""} selected
              {Object.values(kpiSourceMap).filter(Boolean).length > 0 && (
                <span style={{ color: "var(--es-source)" }}> · {Object.values(kpiSourceMap).filter(Boolean).length} source assignment{Object.values(kpiSourceMap).filter(Boolean).length !== 1 ? "s" : ""}</span>
              )}
              {hasPendingChanges && <span style={{ color: "var(--es-source)", fontWeight: 600 }}> · unsaved changes</span>}
              {(isViewOnly || !canUpdate) && <span style={{ color: "var(--es-amber-700)", fontWeight: 600 }}> · view only</span>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {fyData.length > 0 && (
            <SimpleDropdown
              label="Financial Year"
              value={selectedFY?.id ?? ""}
              onChange={val => {
                const found = fyData.find(f => String(f.id) === String(val));
                if (found) setSelectedFY(found);
              }}
              options={fyData.map(fy => ({
                value: fy.id,
                label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}`,
              }))}
              isAlert={selectedFY?.id !== latestFY?.id}
            />
          )}
          <PrimaryButton onClick={handleSave} disabled={saving || !hasPendingChanges || isDisableEdit}>
            {saving ? <><Spinner size={12} color="var(--es-white)" /> Saving…</> : saved ? "✓ Saved!" : "Save Preferences"}
          </PrimaryButton>
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: 1040, margin: "0 auto" }}>
        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        <SourceLegend sources={sources} kpiSourceMap={kpiSourceMap} pendingIds={pendingIds} />

        <div style={{ display: "flex", gap: 0, background: "var(--es-slate-200)", padding: 3, borderRadius: 10, width: "fit-content", marginBottom: 20 }}>
          {[
            { key: "list", label: "All KPIs", count: allQuestions.length },
            { key: "selected", label: "Selected", count: sourceAssignedCount }
          ].map(tab => (
            <button key={tab.key} onClick={() => setKpiView(tab.key)} style={{
              padding: "7px 20px", border: "none", borderRadius: 8,
              background: kpiView === tab.key ? "var(--es-white)" : "transparent",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: kpiView === tab.key ? "var(--es-slate-900)" : "var(--es-slate-500)",
              display: "flex", alignItems: "center", gap: 7,
              boxShadow: kpiView === tab.key ? "0 1px 3px var(--es-shadow-md)" : "none", transition: "all 0.15s"
            }}>
              {tab.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: kpiView === tab.key ? (tab.key === "selected" ? "var(--es-blue-100)" : "var(--es-slate-100)") : "var(--es-gray-300)",
                color: kpiView === tab.key ? (tab.key === "selected" ? "var(--es-blue-700)" : "var(--es-slate-600)") : "var(--es-slate-400)"
              }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {kpiView === "list" && (
          <>
            <div style={{ background: "var(--es-white)", padding: "16px", borderRadius: 12, border: "1px solid var(--es-slate-200)", marginBottom: 12, boxShadow: "0 1px 3px var(--es-shadow-xs)" }}>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "var(--es-slate-400)" }}>⌕</span>
                <input
                  className="es-input"
                  value={search}
                  onChange={e => !isViewOnly && setSearch(e.target.value)}
                  readOnly={isViewOnly}
                  placeholder="Search by title or module…"
                  style={{
                    width: "100%", padding: "9px 12px 9px 32px", borderRadius: 9, fontSize: 13, outline: "none", color: "var(--es-slate-800)",
                    background: isViewOnly ? "var(--es-slate-50)" : "var(--es-white)", cursor: isViewOnly ? "default" : "text"
                  }}
                />
              </div>
              <KpiFilterBar
                allQuestions={allQuestions}
                filterSteps={filterSteps}
                onFilterStepsChange={setFilterSteps}
                pendingIds={pendingIds}
                disabled={isDisableEdit}
                sources={sources}
                kpiSourceMap={kpiSourceMap}
                onKpiSourceChange={handleKpiSourceChange}
                onSelectFiltered={handleSelectFiltered}
              />
            </div>

            {showKpiList ? (
              <>
                {search.trim().length > 0 && !isDisableEdit && sources.length > 0 && (
                  <BulkSourceAssignBar
                    sources={sources}
                    visibleQuestions={visibleKpis}
                    pendingIds={pendingIds}
                    kpiSourceMap={kpiSourceMap}
                    onKpiSourceChange={handleKpiSourceChange}
                    disabled={isDisableEdit}
                  />
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, padding: "0 2px" }}>
                  <span style={{ fontSize: 11, color: "var(--es-slate-400)" }}>
                    {visibleKpis.length} KPI{visibleKpis.length !== 1 ? "s" : ""}
                    {isLeaf && !isDisableEdit ? " — all auto-selected, uncheck to exclude" : ""}
                    {isDisableEdit ? " — view only" : ""}
                    {search.trim() ? " (search results)" : ""}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--es-cyan-700)", fontWeight: 600 }}>
                    {visibleKpis.filter(q => pendingIds.has(q.questionId)).length} / {visibleKpis.length} selected
                  </span>
                </div>

                <div style={{ background: "var(--es-white)", borderRadius: 12, border: "1px solid var(--es-slate-200)", overflow: "hidden", boxShadow: "0 1px 3px var(--es-shadow-xs)" }}>
                  {visibleKpis.length === 0 ? (
                    <div style={{ padding: "60px 20px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: "var(--es-slate-400)" }}>No KPIs match this filter.</div>
                    </div>
                  ) : (
                    <div style={{ maxHeight: 580, overflowY: "auto" }}>
                      {visibleKpis.map((q, idx) => (
                        <KpiListRow
                          key={q.questionId}
                          kpi={q}
                          checked={pendingIds.has(q.questionId)}
                          onChange={c => toggleKpi(q.questionId, c)}
                          isLast={idx === visibleKpis.length - 1}
                          disabled={isDisableEdit}
                          sources={sources}
                          kpiSourceMap={kpiSourceMap}
                          onKpiSourceChange={handleKpiSourceChange}
                          showSourcePicker={sources.length > 0}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ background: "var(--es-white)", borderRadius: 12, border: "1px dashed var(--es-slate-200)", boxShadow: "0 1px 3px var(--es-shadow-xs)", padding: "48px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--es-slate-400)", marginBottom: 6 }}>
                  {activeSteps.length === 0 ? "Select a filter above to get started" : "Continue selecting filters to view KPIs"}
                </div>
                <div style={{ fontSize: 12, color: "var(--es-slate-300)" }}>
                  KPIs appear once all filter levels are chosen, or search for a specific KPI above
                </div>
              </div>
            )}
          </>
        )}

        {kpiView === "selected" && (
          <div style={{ background: "var(--es-white)", borderRadius: 12, border: "1px solid var(--es-slate-200)", overflow: "hidden", boxShadow: "0 1px 3px var(--es-shadow-xs)" }}>
            <SelectedKpisAccordion
              questions={allQuestions}
              savedIds={savedIds}
              pendingIds={pendingIds}
              onToggle={toggleKpi}
              disabled={isDisableEdit}
              sources={sources}
              kpiSourceMap={kpiSourceMap}
              onKpiSourceChange={handleKpiSourceChange}
            />
          </div>
        )}

        {sources.length > 0 && (
          <div style={{ marginTop: 12, padding: "8px 14px", background: "var(--es-white)", border: "1px solid var(--es-slate-100)", borderRadius: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, color: "var(--es-slate-400)", fontWeight: 700 }}>LEGEND:</span>
            <span style={{ fontSize: 10, color: "var(--es-source)" }}>⚡ = Energy source assigned per KPI (stored individually)</span>
            <span style={{ fontSize: 10, color: "var(--es-cyan-700)" }}>Filter Source Dropdown = assigns to ALL KPIs matching selected filters at once</span>
            <span style={{ fontSize: 10, color: "var(--es-slate-400)" }}>Use "Bulk assign" for search results</span>
          </div>
        )}
      </div>
    </div>
  );
}
